import { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { Grid, Typography, FormHelperText } from '@mui/material'
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  useDroppable,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'

import { MediaInputModal } from './media-input-modal.component'
import { MediaImageDraggable } from './media-image-draggable.component'
import { StubImageButton } from './stub-image-button.component'
import { IMediaFileWithId, IMediaFiles, ETranslationNs } from 'src/types'
import { MediaInputContext } from './context/media.context'
import useStyles from './styles'
import { HelperWrapper } from '../helper-wrapper'
import { MediaSnapImageModal } from './media-snap-image-modal.component'

interface IMediaInputProps {
  inputName?: string
  media?: IMediaFileWithId[]
  setFieldValue: (field: string, value: any) => void
  setIsMediaTouched?: Dispatch<SetStateAction<boolean>>
  errorText?: string
  title?: string
  mainImageSwitch?: boolean
  disabled?: boolean
  disabledBorder?: boolean
  noJourneyOptions?: boolean
  showSwitch?: boolean
  withDnd?: boolean
  scanToEarnMode?: boolean
}

const MediaInput = ({
  disabledBorder,
  disabled,
  inputName = 'media',
  media,
  setFieldValue,
  setIsMediaTouched,
  errorText,
  title,
  mainImageSwitch,
  noJourneyOptions,
  showSwitch,
  withDnd = false,
  scanToEarnMode = false,
}: IMediaInputProps) => {
  const classes = useStyles({ disabledBorder, scanToEarnMode })
  const [open, setOpen] = useState(false)
  const [openSnapImageModal, setOpenSnapImageModal] = useState(false)
  const [image, setImage] = useState<IMediaFileWithId | null>(null)
  const [webcamKey, setWebcamKey] = useState(0)
  const { setNodeRef } = useDroppable({
    id: 'media',
  })
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const mappedMedia = useMemo(() => {
    if (!media) return []
    return media.map((item) => item?.id ?? item.dragId!)
  }, [media])

  const handleImageDeleteClick = (link: string) => {
    setFieldValue(
      inputName,
      media?.filter(({ link: selected }) => link !== selected),
    )
  }

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    setImage(null)
  }

  const handleSnapImageOpen = () => {
    setOpenSnapImageModal(true)
  }

  const handleSnapImageClose = () => {
    if (setIsMediaTouched) {
      setIsMediaTouched(true)
    }
    setOpenSnapImageModal(false)
    setImage(null)
  }

  const handleImageClick = (image: IMediaFileWithId) => () => {
    setImage({ ...image, editId: image.link })
    handleOpen()
  }

  const setMedia = (value: IMediaFiles) => {
    setFieldValue(inputName, value)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over?.id) return
    if (active.id === over?.id) return
    if (!media) return

    const overIndex = over.data?.current?.sortable?.index
    const activeIndex = active.data?.current?.sortable?.index

    const copyMedia = [...media]
    const [itemToMove] = copyMedia.splice(Number(activeIndex), 1)
    copyMedia.splice(overIndex, 0, itemToMove)

    setFieldValue(inputName, copyMedia)
  }

  const filesCount = media?.length

  return (
    <>
      <Grid className={classes.mediaWrapper}>
        {!scanToEarnMode && (
          <Grid className={classes.upperTitleWrapper}>
            <Typography className={classes.inputType}>
              {title || (
                <>
                  <HelperWrapper
                    ns={ETranslationNs.Form}
                    t="media-input-title"
                    isColorInherited
                    verticalPosition={10}
                    size={10}
                  />
                </>
              )}
            </Typography>
          </Grid>
        )}
        {!scanToEarnMode && (
          <Typography className={classes.inputFilesCount}>
            <HelperWrapper
              ns={ETranslationNs.Form}
              t="media-items-count"
              isColorInherited
              size={11}
              verticalPosition={15}
            />
            : {filesCount}
          </Typography>
        )}

        {withDnd ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Grid container className={classes.imagesWrapper} ref={setNodeRef}>
              {!disabled && (
                <Grid>
                  <StubImageButton
                    handleClick={scanToEarnMode ? handleSnapImageOpen : handleOpen}
                  />
                </Grid>
              )}
              <SortableContext items={mappedMedia} strategy={rectSortingStrategy}>
                {media?.map((image) => (
                  <MediaImageDraggable
                    isDisabled={
                      scanToEarnMode
                        ? image.title.includes('barcode-image')
                          ? true
                          : disabled
                        : disabled
                    }
                    key={image?.id || image.dragId || image.link}
                    type={image.type}
                    handleImageClick={handleImageClick(image)}
                    link={image.link}
                    alt={title ?? 'media file'}
                    title={image.title}
                    link_medium={image.link_small}
                    id={image.id || image.dragId}
                    withDnd={withDnd}
                    scanToEarnMode={scanToEarnMode}
                    isBarcodeImage={scanToEarnMode && image.title.includes('barcode-image')}
                    handleRemove={() => {
                      handleImageDeleteClick(image.link)
                    }}
                  />
                ))}
              </SortableContext>
            </Grid>
          </DndContext>
        ) : (
          <Grid container className={classes.imagesWrapper}>
            {!disabled && (
              <Grid>
                <StubImageButton handleClick={scanToEarnMode ? handleSnapImageOpen : handleOpen} />
              </Grid>
            )}
            {media?.map((image) => (
              <MediaImageDraggable
                isDisabled={
                  scanToEarnMode
                    ? image.title.includes('barcode-image')
                      ? true
                      : disabled
                    : disabled
                }
                key={image?.id || image.dragId || image.link}
                type={image.type}
                handleImageClick={handleImageClick(image)}
                link={image.link}
                alt={title ?? 'media file'}
                title={image.title}
                link_medium={image.link_small}
                id={image.id || image.dragId}
                withDnd={withDnd}
                scanToEarnMode={scanToEarnMode}
                isBarcodeImage={scanToEarnMode && image.title.includes('barcode-image')}
                handleRemove={() => {
                  handleImageDeleteClick(image.link)
                }}
              />
            ))}
          </Grid>
        )}

        <FormHelperText error={!!errorText}>
          {errorText && <HelperWrapper t={errorText} />}
        </FormHelperText>
      </Grid>
      <MediaInputContext.Provider
        value={{
          image,
          setImage,
          setMedia,
          inputName,
          media,
          handleDelete: setFieldValue,
          webcamKey,
          setWebcamKey,
        }}
      >
        <>
          <MediaInputModal
            noJourneyOptions={noJourneyOptions}
            mainImageSwitch={mainImageSwitch}
            onClose={handleClose}
            open={open}
            isEdit={!!image?.editId}
            showSwitch={showSwitch}
            scanToEarnMode={scanToEarnMode}
          />
          <MediaSnapImageModal onClose={handleSnapImageClose} open={openSnapImageModal} />
        </>
      </MediaInputContext.Provider>
    </>
  )
}

export { MediaInput }
